import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CategoryIcon from '@mui/icons-material/Category';
import config from '../../../config';

// HR Leave Types master — codes like CL/SL/EL/LWP with paid flag and an
// optional annual cap. Used by Leave Requests + Leave Balances.
export default function LeaveTypes() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    leaveTypeName: '',
    code: '',
    isPaid: true,
    maxPerYear: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/LeaveTypes/${companyId}?includeInactive=${includeInactive}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load leave types', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, includeInactive]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      leaveTypeName: '',
      code: '',
      isPaid: true,
      maxPerYear: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      leaveTypeName: r.leaveTypeName || '',
      code: r.code || '',
      isPaid: !!r.isPaid,
      maxPerYear: r.maxPerYear ?? '',
      isActive: !!r.isActive,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.leaveTypeName.trim() || !form.code.trim()) {
      toast('Name and code are required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: editing?.id || null,
        CompanyId: companyId,
        LeaveTypeName: form.leaveTypeName.trim(),
        Code: form.code.trim().toUpperCase(),
        IsPaid: form.isPaid,
        MaxPerYear: form.maxPerYear === '' ? null : Number(form.maxPerYear),
        IsActive: form.isActive,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/LeaveType`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Save failed', 'error');
        return;
      }
      toast(editing ? 'Updated' : 'Added');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.leaveTypeName}"?`)) return;
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/LeaveType/${r.id}/${companyId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      toast('Deleted');
      load();
    } else toast('Delete failed', 'error');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#ec4899',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CategoryIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Leave Types
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR · {rows.length} record(s)
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1.5}>
          <Stack direction='row' alignItems='center'>
            <Switch
              size='small'
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
              Show inactive
            </Typography>
          </Stack>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ bgcolor: '#ec4899', '&:hover': { bgcolor: '#db2777' } }}>
            Add Leave Type
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Leave Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Paid</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Max/Year</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No leave types yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r, i) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.leaveTypeName}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={r.code}
                          sx={{ bgcolor: '#f1f5f9', fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>{r.isPaid ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{r.maxPerYear ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={r.isActive ? 'Active' : 'Inactive'}
                          sx={{
                            bgcolor: r.isActive ? '#dcfce7' : '#fee2e2',
                            color: r.isActive ? '#166534' : '#991b1b',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => openEdit(r)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            size='small'
                            onClick={() => remove(r)}
                            sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit Leave Type' : 'New Leave Type'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                label='Leave Type Name *'
                size='small'
                fullWidth
                value={form.leaveTypeName}
                onChange={(e) =>
                  setForm({ ...form, leaveTypeName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Code *'
                size='small'
                fullWidth
                value={form.code}
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.toUpperCase() })
                }
                helperText='e.g. CL, SL, EL'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Max Per Year'
                type='number'
                size='small'
                fullWidth
                value={form.maxPerYear}
                onChange={(e) =>
                  setForm({ ...form, maxPerYear: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction='row' alignItems='center' spacing={2}>
                <Stack direction='row' alignItems='center'>
                  <Switch
                    checked={form.isPaid}
                    onChange={(e) =>
                      setForm({ ...form, isPaid: e.target.checked })
                    }
                  />
                  <Typography sx={{ fontSize: '0.85rem' }}>Paid</Typography>
                </Stack>
                <Stack direction='row' alignItems='center'>
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                  <Typography sx={{ fontSize: '0.85rem' }}>Active</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#ec4899', '&:hover': { bgcolor: '#db2777' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
